import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, merge, combineLatest, Subject } from 'rxjs';
import { Point } from 'geojson';
import { MapElement } from '../../../shared/map/map-element.model';
import { VRGroupService } from '../vrgroup.service';
import { map, filter, tap, take, takeWhile, takeUntil } from 'rxjs/operators';

import { MapService, MapLatLng } from '../../../shared/map/map.service';

/**
 * Handles the logic of following a train
 * - the currently followed train state
 * - listens to the latest locations from the VRGroupService and updates the MapService accordingly
 */
@Injectable({
  providedIn: 'root'
})
export class FollowTrainService implements OnDestroy {

  /**
   * The train user has selected for following, updates only when the user changes selection
   */
  private selectedTrain: BehaviorSubject<MapElement<Point> | null>  = new BehaviorSubject(null);
  public selectedTrain$ = this.selectedTrain.asObservable();

  /**
   * Equipment data adapted to use alongside RunningTrain
   */
  private equipmentArrayData$ = this.vrService.equipmentData$
    .pipe(
      filter(lMsg => lMsg.body instanceof Array),
      map(lMsg => lMsg.body)
    );

  /**
   * Filters out the single followed train or locomotive from the runningTrainData$
   */
  public followedTrainOrLocData$ = merge(
    combineLatest(this.vrService.runningTrainData$, this.selectedTrain$),
    combineLatest(this.equipmentArrayData$, this.selectedTrain$)
  )
    .pipe(
      filter(([_, followedTrain]) => followedTrain !== null), // Don't continue if no train is being followed
      // Pulls out the selected RunningTrain from RunningTrain[] or locomotive list
      map(([trains, followedTrain]) => trains.find(train => train._id === followedTrain.id)),
      // As there are two sources (trains and locomotives), sometimes the followedTrain isn't on the lists
      filter(train => train !== undefined)
    );

  private draggedDifferenceCoords: MapLatLng = {lat: 0, lng: 0};

  private ngUnsubscribe = new Subject();

  constructor(private mapService: MapService,
              private vrService: VRGroupService) {
  }

  ngOnDestroy() {
    // Clear following
    this.followTrain(null);
  }

  /**
   * Sets train as followed / unfollows it
   * @param train Train's MapElement or null to disable following
   */
  public followTrain = (train: MapElement<Point> | null) => {
    /**
     * Both followed train and changeCurrentFeature take either element or null
     */
    this.mapService.changeCurrentFeature(train);
    this.selectedTrain.next(train);

    if (train !== null) {
      /**
       * Set train's layer visible
       */
      this.displayMapLayer(train);

      this.mapService.center$
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(async (newCenter: {lat: number, lng: number}) => {
          const latestTrainData = await this.followedTrainOrLocData$.pipe(take(1)).toPromise();
          const trainCoords = {lng: latestTrainData.x, lat: latestTrainData.y};
          this.draggedDifferenceCoords = {lng: trainCoords.lng - newCenter.lng, lat: trainCoords.lat - newCenter.lat };
          // console.log(this.draggedDifferenceCoords.lat, this.draggedDifferenceCoords.lng);

          /**
           * Keeping marker in bounds
           */
          const currentBounds = this.mapService.getBounds();
          const boundsWidth = Math.abs(currentBounds.getSouthWest().lng - currentBounds.getNorthEast().lng);
          const boundsHeight = Math.abs(currentBounds.getSouthWest().lat - currentBounds.getNorthEast().lat);

          const padding = {
            north: 0.96,
            east: 0.98,
            south: 0.93,
            west: 0.98,
          };

          const restrictedBounds = {
            north: trainCoords.lat + (boundsHeight * padding.north),
            east: trainCoords.lng + (boundsWidth * padding.east),
            south: trainCoords.lat - (boundsHeight * padding.south),
            west: trainCoords.lng - (boundsWidth * padding.west)
          };

          const restriction = {
            sw: {
              lat: restrictedBounds.south,
              lng: restrictedBounds.west
            },
            ne: {
              lat: restrictedBounds.north,
              lng: restrictedBounds.east
            }
          };
          // Restrict dragging the marker outside the visible area
          this.mapService.restrictBounds(restriction);
        });
      this.followedTrainOrLocData$.subscribe(trainData => {
        const coords = {lng: trainData.x - this.draggedDifferenceCoords.lng, lat: trainData.y - this.draggedDifferenceCoords.lat};
        // Center map (don't use flyTo(), as it's animated and the train marker's movement isn't)
        this.mapService.setCenter({lat: coords.lat, lng: coords.lng});
      });
    } else { // If null as parameter -> clear following
      // Enable dragging
      this.ngUnsubscribe.next();
      this.ngUnsubscribe.complete();
      this.mapService.restrictBounds(null);
    }
  }

  public highlightCurrentlyFollowedTrain() {
    const selectedTrain = this.selectedTrain.getValue();
    this.mapService.changeCurrentFeature(selectedTrain);
    this.displayMapLayer(selectedTrain);
  }

  private displayMapLayer(train: MapElement<Point>) {
    // Set the selected layer visible
    train.layer.setVisibility(true, true);
  }

}
